import { getAuthContextState } from "@/features/auth/services/auth-context-store";
import {
  type ArchiveNotificationResponseDto,
  type ListNotificationsQueryDto,
  type ListNotificationsResponseDto,
  type MarkAllNotificationsReadRequestDto,
  type MarkAllNotificationsReadResponseDto,
  type MarkNotificationReadResponseDto,
  type NotificationsUnreadCountResponseDto,
} from "@/features/notifications/model/notifications.types";
import { http } from "@/shared/lib/api/http";

type NotificationRequestOptions = {
  workspaceSlug?: string | null;
};

function resolveWorkspaceHeader(options?: NotificationRequestOptions) {
  const workspaceSlug = options?.workspaceSlug ?? getAuthContextState().activeWorkspaceSlug;
  if (!workspaceSlug) return undefined;

  return {
    "x-workspace-slug": workspaceSlug,
  };
}

function resolveActiveWorkspaceId() {
  const context = getAuthContextState();
  return context.activeWorkspaceId ?? context.authMe?.workspaceAccess[0]?.workspace?.id ?? undefined;
}

export async function listNotifications(
  query: ListNotificationsQueryDto,
  options?: NotificationRequestOptions,
) {
  const response = await http.get<ListNotificationsResponseDto>("/notifications", {
    params: query,
    headers: resolveWorkspaceHeader(options),
  });

  return response.data;
}

export async function getNotificationsUnreadCount(options?: NotificationRequestOptions) {
  const response = await http.get<NotificationsUnreadCountResponseDto>("/notifications/unread-count", {
    headers: resolveWorkspaceHeader(options),
  });

  return response.data;
}

export async function markNotificationRead(id: string, options?: NotificationRequestOptions) {
  const response = await http.patch<MarkNotificationReadResponseDto>(
    `/notifications/${id}/read`,
    undefined,
    {
      headers: resolveWorkspaceHeader(options),
    },
  );

  return response.data;
}

export async function markAllNotificationsRead(
  input?: MarkAllNotificationsReadRequestDto,
  options?: NotificationRequestOptions,
) {
  const response = await http.patch<MarkAllNotificationsReadResponseDto>("/notifications/mark-all-read", {
    workspaceId: input?.workspaceId ?? resolveActiveWorkspaceId(),
  }, {
    headers: resolveWorkspaceHeader(options),
  });

  return response.data;
}

export async function archiveNotification(id: string, options?: NotificationRequestOptions) {
  const response = await http.patch<ArchiveNotificationResponseDto>(
    `/notifications/${id}/archive`,
    undefined,
    {
      headers: resolveWorkspaceHeader(options),
    },
  );

  return response.data;
}
